import {cn} from '@sollapay/ui';
import {Button, Icon, STRETCHED_OVERLAY_CLASS} from '@sollapay/ui/components';

import {useDirection} from '@/lib/i18n/hooks';

import type {FC, ReactNode} from 'react';

export interface FoldButtonProps {
  isCollapsed: boolean;
  onToggle: () => void;
  controlsId?: string;
  className?: string;
  children: ReactNode;
}

/** A foldable section's title, stretched over its `relative` header so a click anywhere toggles it. */
export const FoldButton: FC<FoldButtonProps> = ({
  isCollapsed,
  onToggle,
  controlsId,
  className,
  children
}) => (
  <Button
    variant="tertiary"
    aria-expanded={!isCollapsed}
    aria-controls={controlsId}
    onClick={onToggle}
    className={cn(
      'static h-auto p-0 font-bold text-fg hover:bg-transparent hover:text-fg aria-expanded:bg-transparent aria-expanded:text-fg focus-visible:outline-none',
      className,
      STRETCHED_OVERLAY_CLASS
    )}
  >
    {children}
  </Button>
);

export interface FoldChevronProps {
  isCollapsed: boolean;
  className?: string;
}

/** Points down while open and toward the reading start once folded. */
export const FoldChevron: FC<FoldChevronProps> = ({isCollapsed, className}) => {
  // i18n direction, not `rtl:`/`ltr:` variants: those both match an LTR story nested in an RTL one.
  const direction = useDirection();

  return (
    <Icon
      name="chevron-down"
      aria-hidden
      data-slot="fold-chevron"
      className={cn(
        className,
        'motion-safe:transition-transform',
        isCollapsed && (direction === 'rtl' ? 'rotate-90' : '-rotate-90')
      )}
    />
  );
};
